/**
 * shared/life-rail.ts — the Thread's "Life" rail: relevance (nuisance) score,
 * the 6-day visibility window, top-3 selection and the per-chore nudge budget
 * (card FiaOoQ0mb11g · EPIC 2 story 2.4).
 *
 * Two different things live here, and keeping them apart is the point:
 *
 *   - the RAIL is ambient visibility. It shows what is close to coming due,
 *     quietly, and never more than three cards. It spends no budget — every
 *     nudge Pear never sends is a win (epic sequencing: rail before engine).
 *   - a NUDGE is the interrupting kind, and the budget below caps it at
 *     1 push + 1 in-feed per chore per round. One place, so the push engine
 *     that arrives later inherits the same cap instead of inventing its own.
 *
 * Pure functions only: no DOM, no fetch, no Date.now() — the caller passes
 * `now`, which is what makes the score unit-testable
 * (scripts/probe-life-rail.mjs, phase `unit`).
 *
 * Deliberately structural: `LifeChore` is the slice of the API's ChoreItem the
 * rail reads, so this module type-checks from the root tsconfig (shared/),
 * from the web bundle (imported by LifeRail.tsx) and from a bare `node`
 * probe alike, with no Cloudflare types and no `.ts`-extension imports.
 */

/** The slice of ChoreItem the rail needs. Full ChoreItem satisfies it. */
export interface LifeChore {
  choreId: string;
  title: string;
  /** One-off due date, or the recurring row's NEXT occurrence. */
  dueAt: number | null;
  status: 'open' | 'done';
  completedAt: number | null;
  /** Times THIS occurrence has been pushed out (snooze / defer). */
  snoozeCount: number;
  assigneeId?: string | null;
}

/** A chore further out than this never reaches the rail (AC: 6-days-out
 *  chores invisible). Boundary is exclusive: due in < 6 days shows, at or
 *  beyond 6 days does not. */
export const LIFE_RAIL_WINDOW_MS = 6 * 86_400_000;

/** AC: the feed never carries more than 3 visible chore cards. */
export const LIFE_RAIL_MAX_VISIBLE = 3;

const CHORE_DAY_MS = 86_400_000;

/** The two ways a chore can interrupt. */
export type LifeNudgeKind = 'push' | 'in-feed';

/** AC: per-chore nudge cap — 1 push + 1 in-feed max. */
export const LIFE_NUDGE_CAPS: Record<LifeNudgeKind, number> = { push: 1, 'in-feed': 1 };

/** One ledger entry per chore: what it has spent, and in which round.
 *  `round` is `completedAt` — a completion ends the round and re-arms the
 *  budget; a snooze does not (it is the same round pushing itself out, which
 *  is exactly the behaviour the cap exists to stop). */
export interface LifeNudgeEntry {
  round: number;
  push: number;
  inFeed: number;
}

/** `${choreId}` -> spend. Kept in localStorage by the client. */
export interface LifeNudgeLedger {
  [choreId: string]: LifeNudgeEntry;
}

/** Whether a chore may appear on the rail at all: open, dated, and inside the
 *  6-day window (overdue is always inside it — the window opens, never closes
 *  on something already late). */
export function lifeRailEligible(chore: LifeChore, now: number): boolean {
  if (chore.status !== 'open') return false;
  if (chore.dueAt == null) return false;
  return chore.dueAt - now < LIFE_RAIL_WINDOW_MS;
}

/** Proximity: 1 when due now (or already late), falling away as the date
 *  recedes — 0.5 a day out, ~0.14 at the far edge of the window. */
function proximityFactor(chore: LifeChore, now: number): number {
  const delta = (chore.dueAt ?? now) - now;
  if (delta <= 0) return 1;
  return 1 / (1 + delta / CHORE_DAY_MS);
}

/** Overdue: 1 on time, climbing with lateness, capped at 10 days late
 *  (3.0) so one badly missed chore cannot bury the rail forever. */
function overdueFactor(chore: LifeChore, now: number): number {
  const delta = (chore.dueAt ?? now) - now;
  if (delta >= 0) return 1;
  const lateDays = Math.min(-delta / CHORE_DAY_MS, 10);
  return 1 + lateDays / 5;
}

/** Snooze: every push this occurrence has taken makes it louder, not quieter
 *  — a chore that keeps getting moved is the one worth showing. */
function snoozeFactor(chore: LifeChore): number {
  return 1 + Math.max(0, chore.snoozeCount ?? 0) * 0.5;
}

/** Nuisance score = hard-due proximity x overdue x snooze weight.
 *  0 means "not on the rail" — done, undated, or more than 6 days out. */
export function lifeRailScore(chore: LifeChore, now: number): number {
  if (!lifeRailEligible(chore, now)) return 0;
  return proximityFactor(chore, now) * overdueFactor(chore, now) * snoozeFactor(chore);
}

export interface LifeRailSelection {
  /** At most LIFE_RAIL_MAX_VISIBLE chores, highest score first. */
  visible: LifeChore[];
  /** Eligible chores kept off the cards — what "Life (n)" counts. */
  hiddenCount: number;
  /** Every eligible chore, shown or not. */
  eligibleCount: number;
}

/** Rank the eligible chores and cut them to the visible three. Ties fall to
 *  the earlier due date, then the title, so the rail never flickers between
 *  two chores that score the same. */
export function selectLifeRail(
  chores: readonly LifeChore[],
  now: number,
  limit: number = LIFE_RAIL_MAX_VISIBLE,
): LifeRailSelection {
  const ranked = chores
    .filter((chore) => lifeRailEligible(chore, now))
    .map((chore) => ({ chore, score: lifeRailScore(chore, now) }))
    .sort(
      (a, b) =>
        b.score - a.score ||
        (a.chore.dueAt ?? 0) - (b.chore.dueAt ?? 0) ||
        a.chore.title.localeCompare(b.chore.title),
    )
    .map((entry) => entry.chore);
  const visible = ranked.slice(0, Math.max(0, limit));
  return { visible, hiddenCount: ranked.length - visible.length, eligibleCount: ranked.length };
}

/** The round a chore's nudge budget belongs to: its last completion. */
export function lifeNudgeRound(chore: LifeChore): number {
  return chore.completedAt ?? 0;
}

/** Spend one nudge. Returns false when the cap has already been spent for
 *  this kind in this round — the caller then stays quiet. Mutates `ledger`
 *  (the caller owns persistence). */
export function takeLifeNudge(
  ledger: LifeNudgeLedger,
  chore: LifeChore,
  kind: LifeNudgeKind,
): boolean {
  if (chore.status !== 'open') return false;
  const round = lifeNudgeRound(chore);
  let entry = ledger[chore.choreId];
  if (!entry || entry.round !== round) {
    entry = { round, push: 0, inFeed: 0 };
    ledger[chore.choreId] = entry;
  }
  const spent = kind === 'push' ? entry.push : entry.inFeed;
  if (spent >= LIFE_NUDGE_CAPS[kind]) return false;
  if (kind === 'push') entry.push = spent + 1;
  else entry.inFeed = spent + 1;
  return true;
}

/** Drop ledger rows for chores the couple no longer has (deleted, or gone
 *  from the list) so localStorage cannot grow without bound. */
export function pruneLifeNudgeLedger(
  ledger: LifeNudgeLedger,
  chores: readonly LifeChore[],
): LifeNudgeLedger {
  const live = new Set(chores.map((chore) => chore.choreId));
  for (const id of Object.keys(ledger)) if (!live.has(id)) delete ledger[id];
  return ledger;
}
